import React, { useRef, useEffect, useState, useCallback } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import PunchPointsModal from './PunchPointsModal';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { cookie } from 'utils/cookies';

const TransformerForm = ({ role = cookie.get('user-role'), userId = cookie.get('user-id') }) => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showPunchModal, setShowPunchModal] = useState(false);
  const [pendingRework, setPendingRework] = useState(null);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const dataLoadedRef = useRef(false);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      equipment_manufacturer: '',
      rated_output: '',
      serial_number: '',
      equipment_tag: '',

      // Level 1
      visual_checks: Array(17).fill({ status: '', remark: '' }),
      physical_checks: Array(25).fill({ status: '', remark: '' }),
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_office_comments: '',
      l1_other_comments: '',
      l1_status: 'draft',

      // Level 2
      transformer_healthy: '',
      functional_checks_status: '',
      testing_commissioning_status: '',
      insulation_resistance_status: '',
      earth_resistance_status: '',
      protection_alarms_status: '',
      l2_office_comments: '',
      insulation_resistance: [
        { description: 'Primary (HT/HV)- Earth', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
        { description: 'Secondary (LT/LV)- Earth', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
        { description: 'Primary (HT/HV)- Secondary(LT/LV)', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
      ],
      earth_resistance: [
        { description: 'Neutral - Ground', value: '' },
        { description: 'Transformer body - Ground', value: '' },
      ],
      protection_alarms: [
        { component: 'Buchholz Relay', alarm: '', trip: '', remarks: '' },
        { component: 'Winding temp. Indicator', alarm: '', trip: '', remarks: '' },
        { component: 'Oil Temp. Indicator', alarm: '', trip: '', remarks: '' },
        { component: 'Pressure Relief valve', alarm: '', trip: '', remarks: '' },
        { component: 'Magnetic Oil Level Gauge', alarm: '', trip: '', remarks: '' },
      ],
      functional_checks: Array(8).fill({ status: '', remark: '' }),
      instruments: [
        { description: 'IR Tester', serial: '', range: '', calibration: '' },
        { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
        { description: 'Milli – Ampere meter', serial: '', range: '', calibration: '' },
      ],
      energizing_process: {
        completion_date: '',
        completion_time: '',
      },
      remarks: '',
      observations: { a: '', b: '', c: '' },
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_other_comments: '',
      l2_status: 'draft',

      // overall
      status: 'draft',
      punch_points: '',
    },
  });

  const formValues = useWatch({ control });
  const punch_points = useWatch({ control, name: 'punch_points' });
  const l1_status = useWatch({ control, name: 'l1_status' });
  const l2_status = useWatch({ control, name: 'l2_status' });
  const l1_manager_id = useWatch({ control, name: 'l1_manager_id' });
  const l2_manager_id = useWatch({ control, name: 'l2_manager_id' });

  // ---- Per-level editability for core fields (engineer owns 'draft') ----
  const isLevel1Editable = (!l1_manager_id || l1_manager_id === userId) && ((role === 'engineer' && (l1_status === 'draft' || l1_status === 'CONTRACTOR' || l1_status === 'MANAGER')) || (role === 'contractor' && (l1_status === 'draft' || l1_status === 'CONTRACTOR')));
  const isLevel2Editable = (!l2_manager_id || l2_manager_id === userId) && ((role === 'engineer' && (l2_status === 'draft' || l2_status === 'CONTRACTOR' || l2_status === 'MANAGER')) || (role === 'contractor' && (l2_status === 'draft' || l2_status === 'CONTRACTOR')));

  // ---- General comments open for ALL roles until that level is approved ----
  const canEditL1General = l1_status !== 'approved';
  const canEditL2General = l2_status !== 'approved';

  // ---- Contractor can open tab only at CONTRACTOR stage ----
  const disableLevel1Tab = role === 'contractor' && getValues('l1_status') !== 'draft' && getValues('l1_status') !== 'CONTRACTOR';
  const disableLevel2Tab = role === 'contractor' && getValues('l2_status') !== 'draft' && getValues('l2_status') !== 'CONTRACTOR';

  // ---- Signature edit permissions (per level, per role, per stage) ----
  const canEditSignature = useCallback(
    (level, sigRole) => {
      const managerId = level === 'l1' ? getValues('l1_manager_id') : getValues('l2_manager_id');
      if (role !== 'quality' && managerId && managerId !== userId) return false;
      const s = level === 'l1' ? getValues('l1_status') : getValues('l2_status');
      if (sigRole === 'charge_engineer' && role === 'engineer' && (s === 'draft' || s === 'CONTRACTOR' || s === 'MANAGER')) return true;
      if (sigRole === 'contractor' && role === 'contractor' && (s === 'draft' || s === 'CONTRACTOR')) return true;
      if (sigRole === 'quality' && role === 'quality' && s === 'QUALITY') return true;
      return false;
    },
    [role, getValues, userId]
  );

  // ---- Overall status derivation (parity with other updated forms) ----
  const deriveCommonStatus = (s1, s2) => {
    if (s1 === 'CONTRACTOR' && s2 === 'CONTRACTOR') return 'CONTRACTOR';
    if (s1 === 'MANAGER' && s2 === 'MANAGER') return 'MANAGER';
    if (s1 === 'QUALITY' && s2 === 'QUALITY') return 'QUALITY';
    if (s1 === 'approved' && s2 === 'approved') return 'approved';
    return 'draft';
  };

  // ---- Autosave ----
  const autoSave = useCallback(
    _.debounce((data) => {
      if (!dataLoadedRef.current) return;
      const overall = deriveCommonStatus(data.l1_status, data.l2_status);
      const payload = {
        form_data: {
          status: overall,
          form_id: formId,
          formName: 'transformer',
          station_id: data.station_id,
          project_name: data.project_name,
          equipment_manufacturer: data.equipment_manufacturer,
          rated_output: data.rated_output,
          serial_number: data.serial_number,
          equipment_tag: data.equipment_tag,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            office_comments: data.l1_office_comments,
            other_comments: data.l1_other_comments,
            status: data.l1_status,
          },
          l2: {
            transformer_healthy: data.transformer_healthy,
            functional_checks_status: data.functional_checks_status,
            testing_commissioning_status: data.testing_commissioning_status,
            insulation_resistance_status: data.insulation_resistance_status,
            earth_resistance_status: data.earth_resistance_status,
            protection_alarms_status: data.protection_alarms_status,
            office_comments: data.l2_office_comments,
            insulation_resistance: Array.isArray(data.insulation_resistance)
              ? data.insulation_resistance.map((item) => ({
                  ...item,
                  measured_value: item.measured_value_1min || item.measured_value || '',
                }))
              : data.insulation_resistance,
            earth_resistance: data.earth_resistance,
            protection_alarms: data.protection_alarms,
            functional_checks: data.functional_checks,
            instruments: data.instruments,
            energizing_process: data.energizing_process,
            remarks: data.remarks,
            observations: data.observations,
            comments: data.l2_comments,
            other_comments: data.l2_other_comments,
            status: data.l2_status,
          },
          punch_points: data.punch_points,
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => { autoSave(formValues); }, [formValues, autoSave]);

  // ---- Load existing ----
  useEffect(() => {
    if (formId) dispatch({ type: VIEW_FORM, payload: { formId, formName: 'transformer' } });
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('equipment_manufacturer', data.equipment_manufacturer || '');
      setValue('rated_output', data.rated_output || '');
      setValue('serial_number', data.serial_number || '');
      setValue('equipment_tag', data.equipment_tag || '');

      setValue('visual_checks', Array.isArray(data.l1?.visual_checks) ? data.l1.visual_checks : Array(17).fill({ status: '', remark: '' }));
      setValue('physical_checks', Array.isArray(data.l1?.physical_checks) ? data.l1.physical_checks : Array(25).fill({ status: '', remark: '' }));
      setValue('l1_comments', data.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_office_comments', data.l1?.office_comments || '');
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('l1_manager_id', data.l1?.managerId || null);

      setValue('transformer_healthy', data.l2?.transformer_healthy || '');
      setValue('functional_checks_status', data.l2?.functional_checks_status || '');
      setValue('testing_commissioning_status', data.l2?.testing_commissioning_status || '');
      setValue('insulation_resistance_status', data.l2?.insulation_resistance_status || '');
      setValue('earth_resistance_status', data.l2?.earth_resistance_status || '');
      setValue('protection_alarms_status', data.l2?.protection_alarms_status || '');
      setValue('l2_office_comments', data.l2?.office_comments || '');
      setValue('insulation_resistance', Array.isArray(data.l2?.insulation_resistance)
        ? data.l2.insulation_resistance.map((item, idx) => ({
            description: item.description || [
              'Primary (HT/HV)- Earth',
              'Secondary (LT/LV)- Earth',
              'Primary (HT/HV)- Secondary(LT/LV)',
            ][idx] || '',
            applied_voltage: item.applied_voltage || '',
            measured_value_1min: item.measured_value_1min || item.measured_value || '',
            measured_value_10min: item.measured_value_10min || '',
            pi_value: item.pi_value || '',
            measured_value: item.measured_value_1min || item.measured_value || '',
          }))
        : [
          { description: 'Primary (HT/HV)- Earth', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
          { description: 'Secondary (LT/LV)- Earth', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
          { description: 'Primary (HT/HV)- Secondary(LT/LV)', applied_voltage: '', measured_value_1min: '', measured_value_10min: '', pi_value: '' },
        ]);
      setValue('earth_resistance', Array.isArray(data.l2?.earth_resistance)
        ? data.l2.earth_resistance
        : [
          { description: 'Neutral - Ground', value: '' },
          { description: 'Transformer body - Ground', value: '' },
        ]);
      setValue('protection_alarms', Array.isArray(data.l2?.protection_alarms)
        ? data.l2.protection_alarms
        : [
          { component: 'Buchholz Relay', alarm: '', trip: '', remarks: '' },
          { component: 'Winding temp. Indicator', alarm: '', trip: '', remarks: '' },
          { component: 'Oil Temp. Indicator', alarm: '', trip: '', remarks: '' },
          { component: 'Pressure Relief valve', alarm: '', trip: '', remarks: '' },
          { component: 'Magnetic Oil Level Gauge', alarm: '', trip: '', remarks: '' },
        ]);
      setValue('functional_checks', Array.isArray(data.l2?.functional_checks) ? data.l2.functional_checks : Array(8).fill({ status: '', remark: '' }));
      setValue('instruments', Array.isArray(data.l2?.instruments)
        ? data.l2.instruments
        : [
          { description: 'IR Tester', serial: '', range: '', calibration: '' },
          { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
          { description: 'Milli – Ampere meter', serial: '', range: '', calibration: '' },
        ]);
      setValue('energizing_process', data.l2?.energizing_process || { completion_date: '', completion_time: '' });
      setValue('remarks', data.l2?.remarks || '');
      setValue('observations', data.l2?.observations || { a: '', b: '', c: '' });
      setValue('l2_comments', data.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_status', data.l2?.status || 'draft');
      setValue('l2_manager_id', data.l2?.managerId || null);
      setValue('status', data.status || 'draft');
      setValue('punch_points', viewFormData?.punch_points || '');
      dataLoadedRef.current = true;
    }
  }, [viewFormData, stationId, setValue]);

  // ---- Signature helpers (respect role+status gating) ----
  const handleImageUpload = (e, level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, sigRole);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setValue(`${level}_comments.${sigRole}.signature`, reader.result, { shouldDirty: true, shouldValidate: true });
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, sigRole, type) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signatureType`, type, { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    if (type === 'digital') {
      setCurrentSignature({ level, role: sigRole });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, sigRole) => {
    if (!canEditSignature(level, sigRole)) return toast.info('This signature is not editable for your role/stage');
    setValue(`${level}_comments.${sigRole}.signature`, '', { shouldDirty: true, shouldValidate: true });
    setValue(`${level}_comments.${sigRole}.signatureType`, 'upload', { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveSignature = () => {
    const { level, role: sigRole } = currentSignature;
    if (!canEditSignature(level, sigRole)) {
      setShowModal(false);
      return;
    }
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${level}_comments.${sigRole}.signature`, signatureData, { shouldDirty: true, shouldValidate: true });
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  // ---- Submit transitions (per level) ----
  const nextStatusFor = (curr, action) => {
    // action: 'pending', 'approved', 'draft'
    if (role === 'engineer') return action === 'draft' ? 'draft' : 'CONTRACTOR';
    if (role === 'contractor') return action === 'draft' ? 'CONTRACTOR' : 'MANAGER';
    if (role === 'manager') return action === 'approved' ? 'QUALITY' : 'draft';
    if (role === 'quality') return action === 'approved' ? 'approved' : 'draft';
    return curr || 'draft';
  };

  const onSubmit = (level, action) => (data) => {
    const s1 = data.l1_status;
    const s2 = data.l2_status;
    const l1_next = level === 'l1' ? nextStatusFor(s1, action) : s1;
    const l2_next = level === 'l2' ? nextStatusFor(s2, action) : s2;
    const overall = deriveCommonStatus(l1_next, l2_next);

    if (level === 'l1') setValue('l1_status', l1_next);
    if (level === 'l2') setValue('l2_status', l2_next);
    setValue('status', overall);

    autoSave.cancel();

    const payload = {
      form_data: {
        status: overall,
        form_id: formId,
        formName: 'transformer',
        station_id: data.station_id,
        project_name: data.project_name,
        equipment_manufacturer: data.equipment_manufacturer,
        rated_output: data.rated_output,
        serial_number: data.serial_number,
        equipment_tag: data.equipment_tag,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          office_comments: data.l1_office_comments,
          other_comments: data.l1_other_comments,
          status: l1_next,
        },
        l2: {
          transformer_healthy: data.transformer_healthy,
          functional_checks_status: data.functional_checks_status,
          testing_commissioning_status: data.testing_commissioning_status,
          insulation_resistance_status: data.insulation_resistance_status,
          earth_resistance_status: data.earth_resistance_status,
          protection_alarms_status: data.protection_alarms_status,
          office_comments: data.l2_office_comments,
          insulation_resistance: Array.isArray(data.insulation_resistance)
            ? data.insulation_resistance.map((item) => ({
                ...item,
                measured_value: item.measured_value_1min || item.measured_value || '',
              }))
            : data.insulation_resistance,
          earth_resistance: data.earth_resistance,
          protection_alarms: data.protection_alarms,
          functional_checks: data.functional_checks,
          instruments: data.instruments,
          energizing_process: data.energizing_process,
          remarks: data.remarks,
          observations: data.observations,
          comments: data.l2_comments,
          other_comments: data.l2_other_comments,
          status: l2_next,
        },
        punch_points: data.punch_points,
      },
    };

    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    const label = action === 'approved' ? 'Approved' : action === 'draft' ? 'Saved as Draft' : 'Submitted';
    toast.success(`Level ${level.toUpperCase()} ${label} Successfully`);
    navigate(-1);
  };

  // ---- Button gating for submit/draft visibility ----
  const submitDisabledL1 = (role === 'engineer' || role === 'contractor') ? (l1_status !== 'draft' && l1_status !== 'CONTRACTOR') : true;
  const submitDisabledL2 = (role === 'engineer' || role === 'contractor') ? (l2_status !== 'draft' && l2_status !== 'CONTRACTOR') : true;

  const canEditPunchPoints = ['manager', 'quality', 'maintenance', 'operation'].includes((role || '').toLowerCase());
  const handleReworkClick = (levelOrAction, actionStr) => (e) => {
    if (e) e.preventDefault();
    if (actionStr) {
      setPendingRework({ level: levelOrAction, action: actionStr });
    } else {
      setPendingRework({ action: levelOrAction });
    }
    setShowPunchModal(true);
  };

  const handleSavePunchPoints = async () => {
    if (pendingRework) {
      setShowPunchModal(false);
      const currentValues = getValues();
      if (pendingRework.level) {
        onSubmit(pendingRework.level, pendingRework.action)(currentValues);
      } else {
        onSubmit(pendingRework.action)(currentValues);
      }
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="page-body container-xl">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-4">Transformer - Site Acceptance Testing</h2>
        <Button variant="outline-warning" size="sm" onClick={() => setShowPunchModal(true)}>
          💬 See Comments
        </Button>
      </div>

      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment & Manufacturer</Form.Label>
              <Form.Control type="text" {...register('equipment_manufacturer')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Rated Output</Form.Label>
              <Form.Control type="text" {...register('rated_output')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number</Form.Label>
              <Form.Control type="text" {...register('serial_number')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Tag</Form.Label>
              <Form.Control type="text" {...register('equipment_tag')} disabled={!isLevel1Editable} />
            </Form.Group>
          </Col>
        </Row>

        
        {punch_points && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between shadow-sm border-0 mb-4" style={{ borderRadius: '12px', background: '#fff9db', color: '#856404' }}>
            <div className="d-flex align-items-center">
              <IoInformationCircleOutline className="me-2 text-warning" size={24} />
              <div>
                <strong className="d-block">Rework Comments Available</strong>
                <span className="small">Please review the comments from the manager/quality team.</span>
              </div>
            </div>
            <Button 
              variant="warning" 
              size="sm" 
              className="rounded-pill px-3 fw-bold shadow-sm"
              onClick={() => setShowPunchModal(true)}
            >
              View Comments
            </Button>
          </Alert>
        )}

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          {/* ---------------- Level 1 ---------------- */}
          <Tab eventKey="level1" title={`Level 1 - Pre-Commissioning (${l1_status})`} disabled={disableLevel1Tab}>
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Ensure the identification of equipment tags is done as per SLD',
                  'Ensure the identification of the manufacturer’s nameplate is done as per GTP',
                  'Is the presence of gaskets at the cable termination box verified?',
                  'Has a visual inspection been conducted to ensure no dents, cracks, or damage on the transformer?',
                  'Are all wheels/rollers assembled and tightened as per the requirement?',
                  'Is double earthing ensured for the marshalling panel, transformer main tank, and cable box?',
                  'Are the marshalling panel door locking arrangements in working condition?',
                  'Has the sealing of all holes/glands of the marshalling panel been checked?',
                  'Has a complete internal assembly check been performed for damage and missing parts in the marshalling panel as per GTP?',
                  'Is the marshalling panel control wiring properly affixed inside the panel?',
                  'Is the position of the tap changer (on-line/off-line) as per the approved GTP/design?',
                  'Is the danger plate affixed at both the primary and secondary sides?',
                  'Has the tightness of power cable termination been checked with proper marking on bolts/busbar?',
                  'Is the location and orientation verified as per design and drawing?',
                  'Has the application of anti-corrosive paint been inspected for all earthing joints?',
                  'Has the switchyard fencing, gate, gravel filling, and removal of vegetation been completed?',
                  'Are danger signages installed at the switchyard fencing?',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`visual_checks_${index}`} value="Yes" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`visual_checks_${index}`} value="No" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`visual_checks_${index}`} value="NA" {...register(`visual_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`visual_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Ensure the transformer tank is checked for any leakage or damage',
                  'Ensure the radiator, valve, etc., are checked for any leakage or damage',
                  'Ensure the bushings are checked for any cracks, leakage, or damage',
                  'Ensure the explosion vent and diaphragm are checked for tightness, leakage, or damage',
                  'Ensure the transformer accessories (Buchholz relay, OTI, WTI, MOG, PRV, etc.) are checked for tightness, leakage, or damage',
                  'Ensure the tightness of control wiring inside the marshalling box is checked',
                  'Ensure the components inside the marshalling box are checked for any damage',
                  'Verify that the oil level in the conservator is up to the mark',
                  'Ensure the transformer is properly placed on the rail/channel as per the layout drawing',
                  'Ensure the transformer wheels/rollers are locked on both sides after final fixing',
                  'Ensure the inspection of transformer earthing pits is completed as per GTP/drawing',
                  'Ensure all earthing connections for the body and neutral are properly connected as per GTP/drawings',
                  'Ensure the correctness and tightness of earthing joints are checked',
                  'Verify that the switchyard earth resistance is not more than 1 Ohm',
                  'Ensure the breather is installed along with an oil seal and dry silica gel',
                  'Ensure the inspection covers and gasketed joints are checked',
                  'Verify that all thermometer pockets are filled with transformer oil',
                  'Ensure the Buchholz relay is mounted at an inclined angle of 5 to 7 degrees',
                  'Ensure all sampling, drain, and filter valves are closed, and the oil-filling cap on the main conservator is properly fitted',
                  'Ensure the cable size and number of cables are checked as per SLD',
                  'Ensure the cable gland is properly tightened and earthed',
                  'Ensure the identification of the phase sequence in the cable termination box is verified',
                  'Ensure the installation of radiator cooling fans is completed',
                  'Ensure the anti-vibration pad is checked',
                  'Ensure all external components/devices identification labels are made of stainless-steel material',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`physical_checks_${index}`} value="Yes" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="No" type="radio" name={`physical_checks_${index}`} value="No" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                      <Form.Check inline label="NA" type="radio" name={`physical_checks_${index}`} value="NA" {...register(`physical_checks.${index}.status`)} disabled={!isLevel1Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`physical_checks.${index}.remark`)} disabled={!isLevel1Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l1_other_comments')} disabled={!canEditL1General} placeholder="Enter any additional comments or remarks for Level 1" />
            </Form.Group>

            <h3 className="mt-4">4.1 Level 1 Pre-Commissioning Comments (For Office)</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l1_office_comments')} disabled={!canEditL1General} placeholder="Enter comments for office use" />
            </Form.Group>

            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l1', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l1_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l1_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l1', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l1_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l1', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l1_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l1_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l1', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l1_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Level 1 actions */}
            {role === 'manager' && l1_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l1_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l1', 'draft')}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l1', 'pending'))} disabled={submitDisabledL1}>
                  Submit Level 1
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleReworkClick('l1', 'draft')} disabled={submitDisabledL1}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>

          {/* ---------------- Level 2 ---------------- */}
          <Tab eventKey="level2" title={`Level 2 - Commissioning (${l2_status})`} disabled={disableLevel2Tab}>
            <h3 className="mt-4">6. Commissioning Part</h3>
            <p>
              <strong>Note:</strong> Ensure all below points are in co-ordinate with ANNEXURE: TRANSFORMER: COMMISSIONING PART: Level 2 and if all tests are found correct select Yes / No.
            </p>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>TRANSFORMER FOUND HEALTHY</Form.Label>
                  <Form.Select {...register('transformer_healthy')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Functional Checks</Form.Label>
                  <Form.Select {...register('functional_checks_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>a) INSULATION RESISTANCE TEST</Form.Label>
                  <Form.Select {...register('insulation_resistance_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>b) EARTH RESISTANCE TEST</Form.Label>
                  <Form.Select {...register('earth_resistance_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>c) PROTECTION & ALARMS</Form.Label>
                  <Form.Select {...register('protection_alarms_status')} disabled={!isLevel2Editable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">6.1 Level 2 Commissioning Comments (For Office)</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_office_comments')} disabled={!canEditL2General} placeholder="Enter comments for office use" />
            </Form.Group>

            <h3 className="mt-4">7. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Instrument Description</th>
                  <th>Instrument Serial No.</th>
                  <th>Range</th>
                  <th>Calibration Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'IR Tester',
                  'Digital Multi meter / Tong Tester',
                  'Milli – Ampere meter',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.serial`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.range`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`instruments.${index}.calibration`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Functional Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Functional check Buchholz relay practically by injecting air (by hand pump/compressed O2/oil draining / by simulation) from test cock or draining oil from Buchholz relay by closing both side valve',
                  'A. Operation of Transformer WTI alarm to remote panel\nB. Operation of Transformer WTI trip to remote panel',
                  'A. Operation of Transformer OTI alarm to remote panel\nB. Operation of Transformer OTI trip to remote panel',
                  'A. Operation of Transformer Buchholz alarm to remote panel\nB. Operation of Transformer Buchholz trip to remote panel',
                  'A. Operation of Transformer PRV\nB. Operation of Transformer PRV trip to remote panel',
                  'Final insulation test along with cable before energizing:\na. HT / HV Winding – with Ground\nb. LT / LV winding – with Ground\nc. HT & LT Winding',
                  'Functional check of marshalling panel Door switch and LED light',
                  'Functional check the cooling fans running direction to get air flow towards radiator',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{desc}</td>
                    <td>
                      <Form.Check inline label="Yes" type="radio" name={`functional_checks_${index}`} value="Yes" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="No" type="radio" name={`functional_checks_${index}`} value="No" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                      <Form.Check inline label="NA" type="radio" name={`functional_checks_${index}`} value="NA" {...register(`functional_checks.${index}.status`)} disabled={!isLevel2Editable} />
                    </td>
                    <td>
                      <Form.Control type="text" {...register(`functional_checks.${index}.remark`)} disabled={!isLevel2Editable} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">9. Testing & Commissioning (Electrical Checks / Tests)</h3>
            <h4>I) Insulation Resistance Test (Minimum value - 2 MΩ)</h4>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Applied Voltage</th>
                  <th>Mesure Value in 1 min (G Ω / H Ω)</th>
                  <th>Mesure Value in 10 min (G Ω / H Ω)</th>
                  <th>PI Value</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Primary (HT/HV)- Earth',
                  'Secondary (LT/LV)- Earth',
                  'Primary (HT/HV)- Secondary(LT/LV)',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td><Form.Control type="text" {...register(`insulation_resistance.${index}.applied_voltage`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`insulation_resistance.${index}.measured_value_1min`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`insulation_resistance.${index}.measured_value_10min`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`insulation_resistance.${index}.pi_value`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4 className="mt-4">II) Earth Resistance Test</h4>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>ER (Ohm)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Neutral - Ground',
                  'Transformer body - Ground',
                ].map((desc, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{desc}</td>
                    <td><Form.Control type="text" {...register(`earth_resistance.${index}.value`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h4 className="mt-4">III) Protection & Alarms</h4>
            <p><strong>NOTE:</strong> Test the Tripping of protection devices by actual operation: Tripping of HV / LV breakers checked through all below devices:</p>
            <Table bordered>
              <thead>
                <tr>
                  <th>S.N.</th>
                  <th>Components / Device</th>
                  <th>Alarm</th>
                  <th>Trip</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Buchholz Relay',
                  'Winding temp. Indicator',
                  'Oil Temp. Indicator',
                  'Pressure Relief valve',
                  'Magnetic Oil Level Gauge',
                ].map((component, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{component}</td>
                    <td><Form.Control type="text" {...register(`protection_alarms.${index}.alarm`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`protection_alarms.${index}.trip`)} disabled={!isLevel2Editable} /></td>
                    <td><Form.Control type="text" {...register(`protection_alarms.${index}.remarks`)} disabled={!isLevel2Editable} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">10. Energizing Process</h3>
            <ul>
              <li>After all checks and tests are completed satisfactorily, vent the air from all plugs/screws on the transformer, radiators, bushings, Buchholz relay, and oil surge relay.</li>
              <li>Energize the transformer and maintain it in a no-load condition for 24 hours (as per the manufacturer’s manual).</li>
              <li>Monitor for high magnetizing current, humming sounds, and chattering of the arcing horn during this period.</li>
              <li>After 24 hours, de-energize the transformer and vent the air again to release any trapped air.</li>
              <li>Re-energize the transformer and maintain it in a no-load condition for an additional 6 hours.</li>
              <li>After 6 hours, the transformer can be gradually put on load.</li>
              <li>
                All pre-commissioning and commissioning tests were successfully completed on
                <Form.Control type="date" {...register('energizing_process.completion_date')} disabled={!isLevel2Editable} style={{ display: 'inline-block', width: '150px', margin: '0 10px' }} />
                at
                <Form.Control type="time" {...register('energizing_process.completion_time')} disabled={!isLevel2Editable} style={{ display: 'inline-block', width: '100px', margin: '0 10px' }} />
                , with no abnormalities observed in Visual, Physical & Functional Tests.
              </li>
            </ul>

            <h3 className="mt-4">11. Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Label>The above test results were found satisfactory, and the Transformer can be charged. TRANSFORMER FOUND HEALTHY:</Form.Label>
              <Form.Select {...register('remarks')} disabled={!isLevel2Editable}>
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
            </Form.Group>

            <h3 className="mt-4">12. Observations during Commissioning / Testing (If Any)</h3>
            {['a', 'b', 'c'].map((key) => (
              <Form.Group className="mb-3" key={key}>
                <Form.Label>{key.toUpperCase()}.</Form.Label>
                <Form.Control type="text" {...register(`observations.${key}`)} disabled={!isLevel2Editable} />
              </Form.Group>
            ))}

            <h3 className="mt-4">13. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((r) => {
                      const editable = canEditSignature('l2', r);
                      if (field === 'Date') {
                        return (
                          <td key={r}>
                            <Form.Control type="date" {...register(`l2_comments.${r}.date`)} disabled={!editable} />
                          </td>
                        );
                      } else if (field === 'Signature') {
                        return (
                          <td key={r}>
                            <Form.Select
                              {...register(`l2_comments.${r}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l2', r, e.target.value)}
                              className="mb-2"
                              disabled={!editable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {_.get(formValues, `l2_comments.${r}.signatureType`) === 'upload' && (
                              <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'l2', r)} className="mb-2" disabled={!editable} />
                            )}
                            {_.get(formValues, `l2_comments.${r}.signature`) && (
                              <div>
                                <img src={_.get(formValues, `l2_comments.${r}.signature`)} alt="Signature" style={{ maxWidth: '100px', marginTop: '5px' }} />
                                <Button variant="danger" size="sm" onClick={() => handleResetSignature('l2', r)} className="mt-2" disabled={!editable}>
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={r}>
                          <Form.Control type="text" {...register(`l2_comments.${r}.${field.toLowerCase()}`)} disabled={!editable} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">14. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control as="textarea" rows={4} {...register('l2_other_comments')} disabled={!canEditL2General} placeholder="Enter any additional comments or remarks for Level 2" />
            </Form.Group>

            {/* Level 2 actions */}
            {role === 'manager' && l2_status === 'MANAGER' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Approve (Send to QUALITY)
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Re-Work
                </Button>
              </div>
            ) : role === 'quality' && l2_status === 'QUALITY' ? (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'approved'))} className="me-2">
                  Final Approve
                </Button>
                <Button variant="danger" onClick={handleReworkClick('l2', 'draft')}>
                  Apply for Resubmit
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                <Button variant="success" onClick={handleSubmit(onSubmit('l2', 'pending'))} disabled={submitDisabledL2}>
                  Submit Level 2
                </Button>
                <Button variant="secondary" style={{ marginLeft: '10px' }} onClick={handleReworkClick('l2', 'draft')} disabled={submitDisabledL2}>
                  Save As Draft
                </Button>
              </div>
            )}
          </Tab>
        </Tabs>

        {/* Signature modal (respects per-role/stage gating) */}
        <Modal
          show={
            showModal &&
            ((currentSignature.level === 'l1' && canEditSignature('l1', currentSignature.role)) ||
              (currentSignature.level === 'l2' && canEditSignature('l2', currentSignature.role)))
          }
          onHide={handleCloseModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2">
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>

      <PunchPointsModal
        show={showPunchModal}
        onHide={() => { setShowPunchModal(false); setPendingRework(null); }}
        value={punch_points}
        onChange={(val) => setValue('punch_points', val)}
        onSave={handleSavePunchPoints}
        canEdit={canEditPunchPoints && !!pendingRework}
      />
    </div>
  );
};

TransformerForm.propTypes = {
  role: PropTypes.oneOf(['engineer', 'contractor', 'manager', 'quality', 'admin']),
  userId: PropTypes.string,
};

export default TransformerForm;
